
import { Injectable } from '@angular/core';
import { ISupplementProtocolDetail, SupplementCategory, SupplementProtocol, User } from '../models';
import { HelperService } from './helper.service';
import { DatabaseService } from './database.service';
import { StorageService } from './storage.service';
import { ThenableReference } from '@angular/fire/database';
import { LocalAttachment } from './camera.service';

@Injectable({
    providedIn: 'root'
})
  

export class SupplementProtocolService {

  constructor(
    private dbService: DatabaseService,
    private helperService: HelperService,
    private storageService: StorageService
  ) { }

  /* Organization Supplement Protocols */
  getOrganizationSupplementProtocolList(organizationId: string) {
    return this.dbService.getListByRef<SupplementProtocol>(`organizationSupplementProtocols/${organizationId}`, "id")
  }

  async getOrganizationSupplementProtocolsAsPromise(orgId: string): Promise<SupplementProtocol[]> {
    const snap = await this.dbService.getSnapshot(`organizationSupplementProtocols/${orgId}`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map(sp => {return new SupplementProtocol(sp)}) : null;
  }

  async getOrganizationSupplementProtocolAsPromise(organizationId: string, programId: string): Promise<SupplementProtocol> {
    const snap = await this.dbService.getSnapshot(`organizationSupplementProtocols/${organizationId}/${programId}`);
    return snap? new SupplementProtocol({id: programId, ...snap}) : null;
  }

  async saveOrganizationSupplementProtocol(orgID: string, programId: string, protocolDetail: ISupplementProtocolDetail, modifiedBy: string): Promise<ThenableReference> {
    let result;
    let protocol = {
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      organization: orgID,
      draftSupplementProtocol: protocolDetail
    };
    if(programId === "add") {
      result = await this.dbService.pushByRef(`/organizationSupplementProtocols/${orgID}`, {
        ...protocol,
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        organization: orgID,
      });
    } else {
      result = await this.dbService.updateRef(`/organizationSupplementProtocols/${orgID}/${programId}`, protocol);
    }
    return result
  }

  async publishOrganizationSupplementProtocol(orgID: string, programId: string, supplementProtocolDetail: ISupplementProtocolDetail, modifiedBy: string): Promise<ThenableReference> {
    let result;
    let protocol = {
      ...supplementProtocolDetail,
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      draftSupplementProtocol: null,
      published: new Date().toISOString(),
    };
    if(!programId || programId === "add") {
      result = await this.dbService.pushByRef(`/organizationSupplementProtocols/${orgID}`, {
        ...protocol,
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        organization: orgID,
      });
    } else {
      result = await this.dbService.updateRef(`/organizationSupplementProtocols/${orgID}/${programId}`, protocol);
    }
    return result
  }

  deleteOrganizationSupplementProtocol(organizationId: string, protocolId: string): Promise<void> {
    return this.dbService.removeRef(`/organizationSupplementProtocols/${organizationId}/${protocolId}`);
  }

  /* Supplement Categories */
  getOrganizationSupplementCategoryList(organizationId: string) {
    return this.dbService.getListByRef<SupplementCategory>(`organizationSupplementCategories/${organizationId}`, "id");
  }

  
  async getOrganizationSupplementCategoriesAsPromise(orgId: string): Promise<SupplementCategory[]> {
    const snap = await this.dbService.getSnapshot(`organizationSupplementCategories/${orgId}`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : null;
  }

  async getOrganizationSupplementCategoryAsPromise(organizationId: string, categoryId: string): Promise<SupplementCategory> {
    const snap = await this.dbService.getSnapshot(`organizationSupplementCategories/${organizationId}/${categoryId}`);
    return snap? {id: categoryId, ...snap} : null;
  }

  async publishOrganizationSupplementCategory(orgID: string, programId: string, category: Partial<SupplementCategory>, modifiedBy: string): Promise<ThenableReference> {
    let result;
    let SC = {
      ...category,
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
    };
    if(!programId || programId === "add") {
      result = await this.dbService.pushByRef(`/organizationSupplementCategories/${orgID}`, {
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        organization: orgID,
        ...SC
      })
    } else {
      result = await this.dbService.updateRef(`/organizationSupplementCategories/${orgID}/${programId}`, SC)
    }
    return result
  }

  deleteOrganizationSupplementCategory(organizationId: string, categoryId: string): Promise<void> {
    return this.dbService.removeRef(`/organizationSupplementCategories/${organizationId}/${categoryId}`);
  }

  /* User Supplement Protocols */
  async getSupplementProtocolAsPromise(userId: string = null, protocolId: string) {
    const snap = await this.dbService.getSnapshot(`/supplementProtocols/${userId}/${protocolId}`);
    return snap? new SupplementProtocol({
      ...snap,
      id: protocolId
    }) : null;
  }

  getSupplementProtocolCommentsList(userId: string, planId: string) {
    return this.dbService.getListByRefTest(`/supplementProtocols/${userId}/${planId}/comments`);
  }

  AddSuppementProtocolsComment(userId, supplementId, comment) {
    return this.dbService.pushByRef(`/supplementProtocols/${userId}/${supplementId}/comments`, comment);
  }

  async addSupplementProtocolCommentAttachment(
    userId,
    reportId,
    user: User,
    attachment: LocalAttachment,
    organization?: string
  ) {
    const storagePath = `users/${userId}/supplementProtocols/${reportId}/comments/${attachment.name}`;
    let attachmentRef = null;

    if(attachment.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file);
    }

    if(attachment.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }

    if(!attachmentRef) {
      throw new Error('No attachment reference found');
    }

    return this.AddSuppementProtocolsComment(userId, reportId, {
      comment: null,
      creationDate: new Date().toISOString(),
      createdBy: user.uid,
      createdByName: user.getFullName(),
      organization: organization || null,
      attachments: [{
        url: attachmentRef,
        fileName: attachment.name,
        type: attachment.type,
      }]
    });
  }  

  updateSuppementProtocolsComment(userId: string, supplementId: string, commentId: string, comment) {
    return this.dbService.updateRef(`/supplementProtocols/${userId}/${supplementId}/comments/${commentId}`, comment);
  }

  deleteSuppementProtocolsComment(userId: string, supplementId: string, commentId: string) {
    return this.dbService.removeRef(`/supplementProtocols/${userId}/${supplementId}/comments/${commentId}`);
  }

  deleteSupplementProtocol(userId: string, programId: string): Promise<any> {
    return this.dbService.removeRef(`/supplementProtocols/${userId}/${programId}`);
  }
  
  async getSupplementProtocolsAsPromise(userId): Promise<SupplementProtocol[]> {
    const snap = await this.dbService.getSnapshot(`/supplementProtocols/${userId}`);
    let protocols = this.helperService.transformFirebaseObjectToArray(snap);
    return protocols.map((protocol) => {
      return new SupplementProtocol(protocol)
    });
  }

  async getPublishedSupplementProtocols(userId: string): Promise<SupplementProtocol[]> {
    let protocols = await this.getSupplementProtocolsAsPromise(userId);
    return protocols.filter((protocol: SupplementProtocol) => {
      return protocol.isPublished() || protocol.createdBy === userId;
    }).sort((a: SupplementProtocol, b: SupplementProtocol) => {
      return +new Date(b.startDate) - +new Date(a.startDate);
    });;
  }

  getUserSupplementProtocolList(userId: string) {
    return this.dbService.getListByRefTest<SupplementProtocol>(`supplementProtocols/${userId}`);
  }

  async saveUserSupplementProtocol(orgId: string, userId, programId, supplementProtocolDetail: ISupplementProtocolDetail, modifiedBy: string): Promise<ThenableReference> {
    let result;
    let supplementProtocol = {
      draftSupplementProtocol: supplementProtocolDetail,
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      organization: orgId || null,
    };
    if(programId === "add") {
      result = await this.dbService.pushByRef(`/supplementProtocols/${userId}`, {
        ...supplementProtocol,
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
      })
    } else {
      result = await this.dbService.updateRef(`/supplementProtocols/${userId}/${programId}`, supplementProtocol);
    }
    return result
  }

  async publishUserSupplementProtocol(orgId: string, userId, programId, supplementProtocolDetail: ISupplementProtocolDetail, modifiedBy: string, notification = true): Promise<ThenableReference> {
    let result;
    let supplementProtocol: Partial<SupplementProtocol> = {
      ...supplementProtocolDetail,
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      draftSupplementProtocol: null,
    }
    if(notification) {
      supplementProtocol.published = new Date().toISOString();
    }
    if(!programId || programId === "add") {
      result = await this.dbService.pushByRef(`/supplementProtocols/${userId}`, {
        ...supplementProtocol,
        organization: orgId || null,
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
      })
    } else {
      result = await this.dbService.updateRef(`/supplementProtocols/${userId}/${programId}`, supplementProtocol);
    }
    return result
  }

  updateUserSupplementProtocol(userId: string, programId: string, data: Partial<ISupplementProtocolDetail>){
    return this.dbService.updateRef(`supplementProtocols/${userId}/${programId}`, data)
  } 

  updateUserSupplementProtocolDraft(userId: string, programId: string, data: Partial<ISupplementProtocolDetail>){
    return this.dbService.updateRef(`supplementProtocols/${userId}/${programId}/draftSupplementProtocol`, data);
  } 
}
